import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Tag, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { useCategories } from '../api/categories';

const GENDERS = ['male', 'female', 'other'];
// Mirrors backend BIO_* (routes/auth.py): regular users 80–100, Voce Pro 50–600.
export const bioLimits = (user) => (user?.is_verified_creator ? { min: 50, max: 600 } : { min: 80, max: 100 });
const bioOk = (bio, { min, max }) => bio.trim().length >= min && bio.trim().length <= max;

/**
 * ProfileCompletionGate — every logged user (Voce Pro AND regular) without a
 * main category, gender and/or a valid bio must fill them on next access.
 * Blocking like the avatar gate (only "Esci" escapes). Admins and users still
 * in the avatar gate are skipped. Only the MISSING fields are shown and sent.
 */
const ProfileCompletionGate = () => {
  const { t } = useTranslation('home');
  const { user, updateUser, logout } = useAuth();
  const { toast } = useToast();
  const { categories } = useCategories();
  const [slug, setSlug] = useState('');
  const [gender, setGender] = useState('');
  const [bio, setBio] = useState('');
  const [saving, setSaving] = useState(false);

  if (!user || !user.email_verified || user.is_admin) return null;
  if (user.avatar_prompt_required && !user.avatar_url) return null;
  const limits = bioLimits(user);
  const needCategory = !user.category_slug;
  const needGender = !GENDERS.includes(user.gender);
  // One bio per profile: whoever already wrote one never rewrites it here.
  const needBio = !(user.bio || '').trim();
  if (!needCategory && !needGender && !needBio) return null;
  if (needCategory && categories.length === 0) return null;

  const bioLen = bio.trim().length;
  const ready = (!needCategory || slug) && (!needGender || gender) && (!needBio || bioOk(bio, limits));

  const save = async () => {
    if (!ready || saving) return;
    setSaving(true);
    try {
      const patch = {};
      if (needCategory) patch.category_slug = slug;
      if (needGender) patch.gender = gender;
      if (needBio) patch.bio = bio.trim();
      await updateUser(patch);
      toast({ title: t('profile_gate.saved') });
    } catch (err) {
      const msg = err?.response?.data?.detail?.split?.('|')?.[1];
      toast({ title: t('profile_gate.error'), description: msg, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto bg-background/95 backdrop-blur-sm" data-testid="profile-completion-gate">
      <div className="flex min-h-full items-start justify-center p-4 py-6 sm:items-center">
      <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-6 text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full border-2 border-dashed border-amber-400/60 bg-secondary">
          <Tag className="h-7 w-7 gold-text" strokeWidth={1.5} />
        </div>
        <h2 className="text-lg font-bold">{t('profile_gate.title')}</h2>
        <p className="mt-1.5 text-sm text-muted-foreground">
          <Trans t={t} i18nKey="profile_gate.subtitle" values={{ username: user.username }} components={{ 1: <span className="font-semibold" /> }} />
        </p>

        {needCategory && (
          <div className="mt-5 text-left">
            <label className="mb-1 block text-xs font-semibold">{t('profile_gate.category_label')}</label>
            <select
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              data-testid="category-gate-select"
            >
              <option value="" disabled>{t('profile_gate.category_placeholder')}</option>
              {categories.map((c) => (
                <option key={c.slug} value={c.slug}>{c.name}</option>
              ))}
            </select>
          </div>
        )}

        {needGender && (
          <div className="mt-4 text-left">
            <label className="mb-1 block text-xs font-semibold">{t('profile_gate.gender_label')}</label>
            <div className="flex flex-wrap gap-2" data-testid="gender-gate-picker">
              {GENDERS.map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => setGender(g)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                    gender === g ? 'border-amber-500 bg-amber-500/15 gold-text' : 'border-border text-muted-foreground hover:bg-muted'
                  }`}
                  data-testid={`gender-gate-${g}`}
                >
                  {t(`profile_gate.gender_${g}`)}
                </button>
              ))}
            </div>
          </div>
        )}

        {needBio && (
          <div className="mt-4 text-left">
            <label className="mb-1 block text-xs font-semibold">{t('profile_gate.bio_label')}</label>
            <Textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              onFocus={(e) => { const el = e.target; setTimeout(() => el.scrollIntoView({ block: 'center', behavior: 'smooth' }), 250); }}
              maxLength={limits.max}
              rows={3}
              placeholder={t('profile_gate.bio_placeholder')}
              data-testid="bio-gate-textarea"
            />
            <p
              className={`mt-1 text-right text-xs ${bioLen > 0 && bioLen < limits.min ? 'text-red-500' : 'text-muted-foreground'}`}
              data-testid="bio-gate-counter"
            >
              {t('profile_gate.bio_counter', { count: bio.length, max: limits.max, min: limits.min })}
            </p>
          </div>
        )}

        <Button
          onClick={save}
          disabled={!ready || saving}
          className="gold-gradient mt-5 w-full font-bold text-black"
          data-testid="profile-gate-confirm-btn"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('profile_gate.confirm')}
        </Button>
        <button
          onClick={logout}
          className="mt-3 text-xs text-muted-foreground underline-offset-2 hover:underline"
          data-testid="profile-gate-logout-btn"
        >
          {t('profile_gate.logout')}
        </button>
      </div>
      </div>
    </div>
  );
};

export default ProfileCompletionGate;
